import { FC } from "react";

import { Pivot, PivotItem, Stack, useTheme } from "@bps/fluent-ui";
import { Contact } from "@stores/practice/models/Contact.ts";
import { ContactFetcher } from "@ui-components/ContactFetcher.tsx";

import { PatientCardIds } from "../../../../shared-components/types/patient-card-ids.enum.ts";
import { NoDetailsButton } from "../NoDetailsButton.tsx";
import { ContactMethodListAddresses } from "./ContactMethodListAddresses.tsx";
import { ContactMethodListCommunications } from "./ContactMethodListCommunications.tsx";

export interface ContactMethodListFieldProps {
  showEditIcon: boolean;
  patient: Contact;
  onHandleEditModal: (id?: string) => void;
  isOrganisation?: boolean;
}

export const ContactMethodList: FC<ContactMethodListFieldProps> = ({
  onHandleEditModal,
  showEditIcon,
  isOrganisation,
  patient
}) => {
  const isContactMethodsExist =
    patient.addresses.length > 0 || patient.communications.length > 0;

  const theme = useTheme();

  const { linkedOrganisations, isEmployersContactMethodsExist } = patient;

  const displayMethods = () => {
    if (isOrganisation || !linkedOrganisations.length) {
      return (
        <Stack tokens={{ childrenGap: 8 }}>
          <ContactMethodListAddresses contact={patient} />
          <ContactMethodListCommunications
            contact={patient}
            isOrganisation={isOrganisation}
          />
        </Stack>
      );
    } else {
      return (
        <Pivot
          styles={{
            root: {
              ".ms-Pivot-link[data-is-overflowing] ~ .ms-Pivot-overflowMenuButton":
                {
                  color: theme.palette.themePrimary
                },
              display: "flex",
              "& .ms-Icon": {
                fontSize: 18
              }
            },
            linkInMenu: { justifyContent: "start" }
          }}
          overflowBehavior="menu"
        >
          {isContactMethodsExist && (
            <PivotItem headerText="Personal" itemKey="personal">
              <Stack
                styles={{ root: { marginTop: 12 } }}
                tokens={{ childrenGap: 8 }}
              >
                <ContactMethodListAddresses contact={patient} />
                <ContactMethodListCommunications
                  contact={patient}
                  isOrganisation={isOrganisation}
                />
              </Stack>
            </PivotItem>
          )}
          {isEmployersContactMethodsExist &&
            linkedOrganisations
              .sort((a, b) => a.name.localeCompare(b.name))
              .filter(
                x => x.addresses.length > 0 || x.communications.length > 0
              )
              .map(emp => (
                <PivotItem headerText={emp.name} itemKey={emp.id} key={emp.id}>
                  <Stack
                    styles={{ root: { marginTop: 12 } }}
                    tokens={{ childrenGap: 8 }}
                  >
                    <ContactFetcher contactId={emp.id}>
                      {org => (
                        <>
                          {emp.communications.length > 0 && (
                            <ContactMethodListCommunications
                              isOrganisation={true}
                              contact={org}
                            />
                          )}
                          {emp.addresses.length > 0 && (
                            <ContactMethodListAddresses contact={org} />
                          )}
                        </>
                      )}
                    </ContactFetcher>
                  </Stack>
                </PivotItem>
              ))}
        </Pivot>
      );
    }
  };

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      {showEditIcon ? (
        displayMethods()
      ) : (
        <NoDetailsButton
          sectionId={PatientCardIds.contactMethods}
          actionTitle="Add a contact method"
          noPermissionText="No contact methods"
          secondaryText="Phone numbers, Addresses"
          onHandleEditModal={onHandleEditModal}
        />
      )}
    </Stack>
  );
};
